import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Bed, Bath, Square, Calendar, Phone } from "lucide-react";
import { Link } from "wouter";
import type { Property } from "@shared/schema";
import type { ImgHTMLAttributes } from "react";

interface PropertyCardProps {
  property: Property;
  onScheduleTour: (propertyId: string) => void;
  onContactAgent: (propertyId: string) => void;
  onClick?: (property: Property) => void;
  onHover?: (propertyId: string) => void;
  idPrefix?: string;
  imgProps?: ImgHTMLAttributes<HTMLImageElement>;
}

export default function PropertyCard({
  property,
  onScheduleTour,
  onContactAgent,
  onClick,
  onHover,
  idPrefix,
  imgProps,
}: PropertyCardProps) {
  const handleScheduleTour = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onScheduleTour(property.id);
  };

  const handleContactAgent = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onContactAgent(property.id);
  };

  const handleCardClick = () => {
    if (onClick) {
      onClick(property);
    }
  };

  const cardId = `${idPrefix ?? "card-"}${property.id}`;

  const cardElement = (
    <Card
      className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow duration-300 cursor-pointer h-full"
      id={cardId}
      onMouseEnter={() => onHover?.(property.id)}
      onFocus={() => onHover?.(property.id)}
      onTouchStart={() => onHover?.(property.id)}
    >
      {/* Property Image */}
      <div className="relative h-64">
        <img
          src={(property.images && property.images[0]) || "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&h=600&fit=crop"}
          alt={property.title || `${property.city}, ${property.state}`}
          loading={imgProps?.loading ?? "lazy"}
          decoding={(imgProps?.decoding ?? "async") as ImgHTMLAttributes<HTMLImageElement>["decoding"]}
          className="absolute inset-0 w-full h-full object-cover"
          {...imgProps}
        />
        {property.isFeatured && (
          <div className="absolute top-4 right-4">
            <Badge className="bg-luxury-gold text-deep-navy hover:bg-luxury-gold">
              Featured
            </Badge>
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6">
          <div className="text-white font-playfair text-2xl font-bold">
            {property.price ? `$${parseInt(property.price.toString()).toLocaleString()}` : 'Price on request'}
          </div>
        </div>
      </div>

      {/* Property Details */}
      <CardContent className="p-6">
        <h3 className="font-playfair text-xl font-semibold text-deep-navy mb-2 line-clamp-2">
          {property.title}
        </h3>
        <p className="text-gray-600 mb-4 text-sm">
          {property.address || `${property.city}, ${property.state}`}
        </p>
        
        {/* Property Stats */}
        <div className="flex justify-between items-center text-sm text-gray-600 mb-4">
          <div className="flex items-center">
            <Bed className="w-4 h-4 mr-1" />
            <span>{property.bedrooms || 'N/A'} Beds</span>
          </div>
          <div className="flex items-center">
            <Bath className="w-4 h-4 mr-1" />
            <span>{property.bathrooms || 'N/A'} Baths</span>
          </div>
          <div className="flex items-center">
            <Square className="w-4 h-4 mr-1" />
            <span>{property.squareFeet ? property.squareFeet.toLocaleString() : 'N/A'} sqft</span>
          </div>
        </div>
        
        {/* Property Description */}
        {property.description && (
          <div className="mb-4">
            <p className="text-gray-700 text-sm line-clamp-3 leading-relaxed">
              {property.description}
            </p>
          </div>
        )}
        
        {/* Listing Info */}
        <div className="flex justify-between items-center text-xs text-gray-500 mb-4">
          <span>Recently listed</span>
        </div>
        
        {/* Action Buttons */}
        <div className="flex space-x-2">
          <Button
            onClick={handleScheduleTour}
            className="flex-1 bg-ocean-blue text-white hover:bg-bright-blue transition-colors duration-200 text-sm py-2"
            data-testid={`button-schedule-tour-${property.id}`}
          >
            <Calendar className="w-4 h-4 mr-1" />
            Schedule Tour
          </Button>
          <Button
            onClick={handleContactAgent}
            variant="outline"
            className="flex-1 border-ocean-blue text-ocean-blue hover:bg-ocean-blue hover:text-white transition-colors duration-200 text-sm py-2"
            data-testid={`button-contact-agent-${property.id}`}
          >
            <Phone className="w-4 h-4 mr-1" />
            Contact Agent
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  // Conditional wrapper rendering
  if (onClick) {
    return (
      <div onClick={handleCardClick} className="cursor-pointer">
        {cardElement}
      </div>
    );
  }

  return (
    <Link href={`/property/${property.id}`}>
      <div>
        {cardElement}
      </div>
    </Link>
  );
}